import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  TicketIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { TicketIcon as TicketIconSolid } from "@heroicons/react/24/solid";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const TicketList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [viewMode, setViewMode] = useState("list");

  // Mock tickets data
  const tickets = [
    {
      id: "TK-2024-001",
      title: "Unable to access payroll portal after password reset",
      description:
        "Employee cannot log into payroll system despite successful password reset. Error message indicates invalid credentials.",
      priority: "High",
      status: "In Progress",
      category: "Technical",
      created: "2024-07-23T08:00:00Z",
      updated: "2024-07-23T10:30:00Z",
      assignedTo: "Sarah Tech",
      assignedAvatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b510?w=32&h=32&fit=crop&crop=face",
      requester: "John Doe",
      requesterAvatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
      department: "Finance",
      responseTime: "1.2 hours",
      comments: 5,
      tags: ["payroll", "login-issue", "urgent"],
    },
    {
      id: "TK-2024-002",
      title: "Request for leave policy clarification regarding maternity leave",
      description:
        "Need detailed information about maternity leave duration, benefits coverage, and return-to-work policies.",
      priority: "Medium",
      status: "Pending Review",
      category: "HR Policy",
      created: "2024-07-23T06:15:00Z",
      updated: "2024-07-23T09:45:00Z",
      assignedTo: "John HR",
      assignedAvatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face",
      requester: "Maria Garcia",
      requesterAvatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face",
      department: "Nursing",
      responseTime: "3.1 hours",
      comments: 3,
      tags: ["hr-policy", "maternity", "benefits"],
    },
    {
      id: "TK-2024-003",
      title: "Training certificate not generated after course completion",
      description:
        "Completed mandatory safety training but certificate is not available for download in the training portal.",
      priority: "Medium",
      status: "Resolved",
      category: "Training",
      created: "2024-07-22T14:30:00Z",
      updated: "2024-07-23T09:15:00Z",
      assignedTo: "Lisa Training",
      assignedAvatar:
        "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=32&h=32&fit=crop&crop=face",
      requester: "David Johnson",
      requesterAvatar:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=32&h=32&fit=crop&crop=face",
      department: "Radiology",
      responseTime: "0.8 hours",
      comments: 8,
      tags: ["training", "certificate", "portal-issue"],
    },
    {
      id: "TK-2024-004",
      title: "Office equipment replacement - ergonomic chair request",
      description:
        "Current desk chair is causing back pain. Request for ergonomic replacement with proper lumbar support.",
      priority: "Low",
      status: "Open",
      category: "Facilities",
      created: "2024-07-22T10:00:00Z",
      updated: "2024-07-22T16:30:00Z",
      assignedTo: "Mike Facilities",
      assignedAvatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
      requester: "Emily Chen",
      requesterAvatar:
        "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=32&h=32&fit=crop&crop=face",
      department: "Administration",
      responseTime: "6.2 hours",
      comments: 2,
      tags: ["facilities", "ergonomic", "equipment"],
    },
    {
      id: "TK-2024-005",
      title: "Access request for new employee - system permissions",
      description:
        "New hire needs access to EMR system, scheduling software, and department-specific applications.",
      priority: "High",
      status: "In Progress",
      category: "Access Control",
      created: "2024-07-23T07:45:00Z",
      updated: "2024-07-23T11:00:00Z",
      assignedTo: "Alex IT",
      assignedAvatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face",
      requester: "HR Department",
      requesterAvatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face",
      department: "Human Resources",
      responseTime: "2.5 hours",
      comments: 4,
      tags: ["access-control", "new-hire", "permissions"],
    },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "open", label: "Open" },
    { value: "in progress", label: "In Progress" },
    { value: "pending review", label: "Pending Review" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ];

  const priorityOptions = [
    { value: "all", label: "All Priority" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ];

  const categoryOptions = [
    { value: "all", label: "All Categories" },
    { value: "technical", label: "Technical" },
    { value: "hr policy", label: "HR Policy" },
    { value: "training", label: "Training" },
    { value: "facilities", label: "Facilities" },
    { value: "access control", label: "Access Control" },
  ];

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return "text-red-600 bg-red-100 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "low":
        return "text-green-600 bg-green-100 border-green-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "open":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "in progress":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "pending review":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "resolved":
        return "text-green-600 bg-green-100 border-green-200";
      case "closed":
        return "text-gray-600 bg-gray-100 border-gray-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "open":
        return <TicketIcon className="w-4 h-4" />;
      case "in progress":
        return <ArrowPathIcon className="w-4 h-4" />;
      case "pending review":
        return <ClockIcon className="w-4 h-4" />;
      case "resolved":
        return <CheckCircleIcon className="w-4 h-4" />;
      case "closed":
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <TicketIcon className="w-4 h-4" />;
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    }
  };

  // Filter tickets based on current filters
  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || ticket.status.toLowerCase() === statusFilter;
    const matchesPriority =
      priorityFilter === "all" ||
      ticket.priority.toLowerCase() === priorityFilter;
    const matchesCategory =
      categoryFilter === "all" ||
      ticket.category.toLowerCase() === categoryFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />

      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 sm:mb-8">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center">
                    <TicketIconSolid className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary mr-2 sm:mr-3" />
                    Support Tickets
                  </h1>
                  <p className="mt-2 text-sm sm:text-base text-gray-600">
                    Manage and track all support requests
                  </p>
                </div>
                <div className="flex space-x-3">
                  <Link
                    to="/support/tickets/create"
                    className="inline-flex items-center px-3 sm:px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium text-sm sm:text-base"
                  >
                    <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    New Ticket
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {/* Filters and Search */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Search Bar */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 sm:pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-sm sm:text-base"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-xs sm:text-sm"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-xs sm:text-sm"
                >
                  {priorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-xs sm:text-sm"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-xs sm:text-sm"
                >
                  <option value="created">Sort by Created</option>
                  <option value="updated">Sort by Updated</option>
                  <option value="priority">Sort by Priority</option>
                  <option value="status">Sort by Status</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <p className="text-xs sm:text-sm text-gray-600">
                Showing {filteredTickets.length} of {tickets.length} tickets
              </p>
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm text-gray-600">View:</span>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                    viewMode === "list"
                      ? "bg-apollo-primary text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                    viewMode === "grid"
                      ? "bg-apollo-primary text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  Grid
                </button>
              </div>
            </div>
          </div>

          {/* Tickets List */}
          <div className="space-y-3 sm:space-y-4">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-4 sm:p-6">
                  {/* Ticket Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-2">
                        <span className="text-xs sm:text-sm font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {ticket.id}
                        </span>
                        <span
                          className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                            ticket.priority
                          )}`}
                        >
                          <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
                          {ticket.priority}
                        </span>
                        <span
                          className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1">{ticket.status}</span>
                        </span>
                        <span className="px-2 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-xs">
                          {ticket.category}
                        </span>
                      </div>
                      <Link
                        to={`/support/tickets/${ticket.id}`}
                        className="text-base sm:text-lg font-semibold text-gray-900 hover:text-apollo-primary transition-colors block truncate"
                      >
                        {ticket.title}
                      </Link>
                      <p className="text-sm sm:text-base text-gray-600 mt-1 line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 sm:ml-4 flex-shrink-0">
                      <Link
                        to={`/support/tickets/${ticket.id}`}
                        className="p-2 text-gray-400 hover:text-apollo-primary transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </Link>
                    </div>
                  </div>

                  {/* Ticket Meta Information */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6">
                      {/* Requester */}
                      <div className="flex items-center space-x-2">
                        <UserIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <img
                          src={ticket.requesterAvatar}
                          alt={ticket.requester}
                          className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0"
                        />
                        <span className="truncate">{ticket.requester}</span>
                        <span className="text-gray-400 hidden sm:inline">
                          •
                        </span>
                        <span className="truncate hidden sm:inline">
                          {ticket.department}
                        </span>
                      </div>

                      {/* Assigned To */}
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 hidden sm:inline">
                          Assigned to:
                        </span>
                        <img
                          src={ticket.assignedAvatar}
                          alt={ticket.assignedTo}
                          className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex-shrink-0"
                        />
                        <span className="font-medium truncate">
                          {ticket.assignedTo}
                        </span>
                      </div>

                      {/* Comments */}
                      <div className="flex items-center space-x-1 hidden sm:flex">
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        <span>{ticket.comments}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-6">
                      {/* Response Time */}
                      <div className="flex items-center space-x-1 hidden sm:flex">
                        <ClockIcon className="w-4 h-4" />
                        <span>Response: {ticket.responseTime}</span>
                      </div>

                      {/* Created/Updated Time */}
                      <div className="text-gray-500">
                        <span>Created {getTimeAgo(ticket.created)}</span>
                        {ticket.updated !== ticket.created && (
                          <span className="ml-2 hidden sm:inline">
                            • Updated {getTimeAgo(ticket.updated)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {ticket.tags && ticket.tags.length > 0 && (
                    <div className="mt-3 sm:mt-4 flex items-center space-x-2">
                      <TagIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {ticket.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredTickets.length === 0 && (
            <div className="text-center py-8 sm:py-12">
              <TicketIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                No tickets found
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
                {searchQuery ||
                statusFilter !== "all" ||
                priorityFilter !== "all" ||
                categoryFilter !== "all"
                  ? "Try adjusting your search criteria or filters."
                  : "Create your first support ticket to get started."}
              </p>
              <Link
                to="/support/tickets/create"
                className="inline-flex items-center px-3 sm:px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors font-medium text-sm sm:text-base"
              >
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Create New Ticket
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TicketList;
